import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import type { EnhancedUserData } from "../../utils/kit-auth-map-profile";
import { kitAuthMapProfile } from "../../utils/kit-auth-map-profile";

type Profile = {
  active: boolean;
  profileId: string;
  travelBankDetails: TravelBankDetails;
  customerDetails: CustomerDetails;
  creditCards: CreditCard[];
  loyaltyDetails: LoyaltyDetail[];
  travelDocuments: TravelDocument[];
  familyPoolMembers: FamilyPoolMember[];
  termAndConditionAcceptedDate: string;
};
type TravelBankDetails = {
  balance: {
    amount: number;
    currency: string;
  };
  accountNumber: string;
};

type CustomerDetails = {
  firstName: string;
  lastName: string;
  prefix: "MR" | "MRS" | "MS" | "MISS" | "DR";
  gender: "MALE" | "FEMALE" | "UNKNOWN";
  dateOfBirth: string;
  contact: {
    emails: string[];
    addresses: {
      street1: string;
      state: string;
      city: string;
      country: string;
      postcode: string;
    }[];
    phones: {
      type: "MOBILE" | "HOME" | "WORK";
      number: string;
    }[];
  };
};

type CreditCard = {
  type: "CREDIT_CARD" | "DEBIT_CARD";
  number: string;
  holderName: string;
  expirationDate: string;
  cardCode: string;
  tokenAuth: string;
  tokenScheme: string;
  tokenTimestamp: string;
};
type LoyaltyDetail = {
  airline: string;
  membershipID: string;
  accountBalance: number;
  membershipTier: string;
  cobrandCardCodes: string[];
  benefitsDetails: string[];
};
type TravelDocument = Record<string, unknown>;
type FamilyPoolMember = Record<string, unknown>;

export type ProfileData = { profile: Profile };
export type ProfileResponse = {
  data: ProfileData;
  version: string;
  status: {
    transactionStatus: "success" | "failure";
  };
};
export const fetchProfile = async ({
  accessToken,
  url,
}: {
  accessToken: string;
  url: string;
}): Promise<EnhancedUserData | undefined> => {
  try {
    const response = await fetch(url, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      method: "GET",
    });

    const profile = kitAuthMapProfile(
      (await response.json()) as ProfileResponse,
      accessToken,
    );

    return profile;
  } catch {
    /* empty */
  }
};
type UseFetchProfileProperties = {
  accessToken?: string;
  url: string;
  enabled?: boolean;
};

export const useFetchProfile = ({
  accessToken,
  url,
  enabled = true,
}: UseFetchProfileProperties): UseQueryResult<EnhancedUserData | undefined> =>
  useQuery({
    enabled: enabled,
    queryFn: () => fetchProfile({ accessToken: accessToken ?? "", url }),
    queryKey: ["kit-auth-profile", accessToken, url],
  });
